import { atom, selector, useSetRecoilState } from "recoil";
import { createDataRequest, updateDataRequest, getRaceListRequest, getTeeListRequest, issueDataRequest, setBibTagRequest, bulkIssueByClubRequest } from "../../../api/controller/RaceKitController";
import { pageState } from "../../../state/GlobalState";
import { permissionState } from "../../../api/state/AuthState";
import { HasPermission } from "../../../custom/helpers/HasPermission";
import { Badge } from "reactstrap";

export const raceKitListState = atom({
    key: 'raceKitListState',
    default: [],
})
export const raceKitRaceState = atom({
	key: 'raceKitRaceState',
	default:{
		data:[],
		ovf:false,
	}
})
export const raceKitTeeState = atom({
	key: 'raceKitTeeState',
	default:{
		data:[],
		ovf:false,
	}
})
export const raceKitNationalityState = atom({
	key: 'raceKitNationalityState',
	default:{
		data:[],
		ovf:false,
	}
})

export const clubsForActiveEventState = atom({
    key: 'clubsForActiveEventState',
    default: {
        data: [],
        loading: false,
        error: null
    }
})

export const bulkIssueModalState = atom({
    key: 'bulkIssueModalState',
    default: {
        visible: false,
        selectedClub: null,
        receiverName: '',
        receiverIdNumber: '',
        receiverPhone: '',
        loading: false
    }
})

export const viewClubKitsModalState = atom({
    key: 'viewClubKitsModalState',
    default: {
        visible: false,
        selectedClub: null,
        raceKits: [],
        loading: false,
        error: null,
        searchQuery: ''
    }
})

export const noteModalState = atom({
    key: 'noteModalState',
    default: {
        visible: false,
        raceKit: null,
    }
})

// The grid cell truncates a long remark and a title tooltip is unreachable on a
// touchscreen, so the badge itself opens the note. Declared here rather than in
// the modal file because the field config below renders it — importing it the
// other way round would make a cycle.
//
// Renders nothing when there is no note to show, so the caller can mount it
// unconditionally: the permission check below is a hook and cannot sit behind a
// condition in the cell renderer.
const NoteBadge = ({ raceKit }) => {
    const setNoteModal = useSetRecoilState(noteModalState)
    // Anyone who can edit a kit sees every note. The highlight flag exists to
    // decide what interrupts the issue desk, and that distinction is worthless
    // to the person who writes the notes in the first place.
    const highlightAll = HasPermission("update")
    if(! raceKit?.remarks || (! raceKit.highlight_remarks && ! highlightAll)) return null
    return (
        <Badge
            color="danger"
            className="bg-danger ms-1"
            role="button"
            title="View note"
            style={{ cursor: 'pointer' }}
            onClick={(e) => { e.stopPropagation(); setNoteModal({ visible: true, raceKit: raceKit }) }}
        >
            <i className="mdi mdi-alert-circle-outline me-1"></i>NOTE
        </Badge>
    )
}

export const modalConfigDefault = { size: "lg", wizard: false }

export const filtersDefault = []

// Nothing applied on load -- the page shows every kit, as it always has.
export const raceKitFiltersDefault = {
    races: [],
    statuses: [],
    club: null,
    teeSizes: [],
    bibFrom: '',
    bibTo: '',
    bibIssuedFrom: '',
    bibIssuedTo: '',
    notes: '',
}

export const raceKitStatusOptions = [
    { label: 'Pending', value: 'PENDING' },
    { label: 'Bib Issued', value: 'BIB ISSUED' },
    { label: 'Issued', value: 'ISSUED' },
]

export const raceKitNotesOptions = [
    { label: 'Has any note', value: 'any' },
    { label: 'Highlighted notes only', value: 'highlighted' },
    { label: 'No note', value: 'none' },
]

const hasNote = (kit) => !! (kit.remarks && String(kit.remarks).trim() !== '')

/**
 * Does one kit pass the applied filters?
 *
 * Filtering happens here rather than server-side because the grid already holds
 * the whole event and is kept live over websockets -- refetching would cost a
 * round trip, and the filter values would land in the websocket channel name,
 * which is not authorised past the bare RaceKit-Primary.
 *
 * This is mirrored by RaceKitExport::collection() in PHP so an export matches
 * what is on screen. Change one, change the other -- there is no test holding
 * them together, so a change here that is not made there shows up as an export
 * quietly disagreeing with the grid.
 */
export const raceKitMatchesFilters = (kit, f) => {
    if(f.races.length > 0 && ! f.races.includes(kit.race_id)) return false
    if(f.statuses.length > 0 && ! f.statuses.includes(kit.status)) return false
    if(f.club && kit.club !== f.club) return false

    if(f.teeSizes.length > 0){
        // Any tee of a matching size qualifies the kit, a parent's included.
        const sizes = (kit.race_tees || []).map((rt) => rt.tee?.tee_size_id).filter((v) => v !== null && v !== undefined)
        if(! sizes.some((s) => f.teeSizes.includes(s))) return false
    }

    // Bib numbers are strings and not all numeric (kids carry bibs like
    // K30026). A range is a numeric question, so a non-numeric bib cannot
    // answer it and drops out.
    if(f.bibFrom !== '' || f.bibTo !== ''){
        const raw = String(kit.bib_number ?? '')
        if(! /^\d+$/.test(raw)) return false
        const n = parseInt(raw, 10)
        if(f.bibFrom !== '' && n < parseInt(f.bibFrom, 10)) return false
        if(f.bibTo !== '' && n > parseInt(f.bibTo, 10)) return false
    }

    if(f.bibIssuedFrom || f.bibIssuedTo){
        const issued = kit.bib_issued_at ? String(kit.bib_issued_at).slice(0, 10) : null
        if(! issued) return false
        if(f.bibIssuedFrom && issued < f.bibIssuedFrom) return false
        if(f.bibIssuedTo && issued > f.bibIssuedTo) return false
    }

    if(f.notes === 'any' && ! hasNote(kit)) return false
    if(f.notes === 'highlighted' && ! (hasNote(kit) && kit.highlight_remarks)) return false
    if(f.notes === 'none' && hasNote(kit)) return false

    return true
}

export const countActiveRaceKitFilters = (f) => (
    (f.races.length > 0 ? 1 : 0) +
    (f.statuses.length > 0 ? 1 : 0) +
    (f.club ? 1 : 0) +
    (f.teeSizes.length > 0 ? 1 : 0) +
    (f.bibFrom !== '' || f.bibTo !== '' ? 1 : 0) +
    (f.bibIssuedFrom || f.bibIssuedTo ? 1 : 0) +
    (f.notes ? 1 : 0)
)

/** The payload shape RaceKitExport expects, so an export matches the screen. */
export const raceKitFiltersToPayload = (f) => ({
    Club: f.club || null,
    RaceIds: f.races,
    Statuses: f.statuses,
    TeeSizeIds: f.teeSizes,
    BibFrom: f.bibFrom !== '' ? f.bibFrom : null,
    BibTo: f.bibTo !== '' ? f.bibTo : null,
    BibIssuedFrom: f.bibIssuedFrom || null,
    BibIssuedTo: f.bibIssuedTo || null,
    Notes: f.notes || null,
})

export const formModes = {
    create: [ { name: 'Default Create', action: createDataRequest } ], 
    update: [ { name: 'Default Update', action: updateDataRequest, closeOnSubmit: true }, { name: 'Issue Race Kit', action: issueDataRequest, closeOnSubmit: true }, { name: 'Set Bib Tag', action: setBibTagRequest, closeOnSubmit: true } ],
    view: [ { name: 'Default View' } ],
    bulkIssue: [ { name: 'Bulk Issue by Club', action: bulkIssueByClubRequest, closeOnSubmit: true } ],
}

export const raceKitDataStructureState = selector({
    key: 'raceKitDataStructureState',
    get: ({ get }) => (
        [

            { 
                id: "RKN",
                label: 'Name',
                type: 'text',
                order: { table: 1, form: 1 }, 
                section: { id: 1, label: "General", isDisplayed: true },
                table: { customRender: (value, data) => (
                    <div className="d-flex align-items-center">
                        <div className="me-2">{data.data?.name}</div>
                        {data.data?.club && <div className="text-muted">({data.data?.club})</div>}
                        {data.data?.parent_bib && <Badge color="warning" className="bg-warning ms-1">+P</Badge>}
                        <NoteBadge raceKit={data.data} />
                    </div>
                ) },
                create: [{ enabled: true, size: { xs: 12, md: 6 }  }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0'], 
            },
			{ 
                label: 'Id Number',
                type: 'text',
                order: { table: 2, form: 2 }, 
                table: { maxWidth: 130 },
                section: { id: 1, label: "General" },
                create: [{ enabled: true, size: { xs: 12, md: 6 }  }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0'], 
            },
			{ 
                label: 'Email',
                type: 'email',
                order: { form: 2.01 }, 
                section: { id: 1, label: "General" },
                create: [{ enabled: true, size: { xs: 12, md: 6 }  }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0'], 
            },
			{ 
                label: 'Phone Number',
                type: 'text',
                order: { table: 2.1, form: 2.1 }, 
                table: { maxWidth: 150 },
                section: { id: 1, label: "General" },
                create: [{ enabled: true, size: { xs: 12, md: 6 }  }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0'], 
            },
            {
                label: "Date Of Birth",
                type: "datepicker",
                order: { form: 2.2 },
                section: { id: 1, label: "General" },
                create: [{ enabled: true, size: { xs: 12, md: 6 }, dateFormat:"YYYY-MM-DD", options: { altInput: true, altFormat: "F j, Y", dateFormat: "Y-m-d", maxDate: 'today' } }], update: ['same_as:create.0'], view: [ 'same_as:create.0' ],
            },
			{ 
                label: 'Gender',
                type: 'select',
                order: { form: 2.3 }, 
                rootValue: true,
                section: { id: 1, label: "General" },
                create: [{ enabled: true, size: { xs: 12, md: 6 }  }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0'], 
				list: {
                    ovf: false,
                    data: [
                        { label: 'Male', value: 'Male' },
                        { label: 'Female', value: 'Female' },
                    ]
                },
            },
			{ 
                label: 'Nationality',
                type: 'select',
                order: { form: 2.4 }, 
                section: { id: 1, label: "General" },
                rootValue: true,
                create: [{ enabled: true, size: { xs: 12, md: 6 } }], update: ['same_as:create.0', { enabled: false }], view: [{ enabled: true, size: { xs: 12, md: 6 } }], 
				list: get(raceKitNationalityState),
            },
			{ 
                label: 'Club',
                type: 'text',
                order: { form: 2.4 }, 
                section: { id: 1, label: "General" },
                create: [{ enabled: true, size: { xs: 12, md: 6 }  }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0'], 
            },
			{ 
                label: 'Bib Number',
                type: 'text',
                order: { table: 3, form: 3 }, 
                table: { maxWidth: 130 },
                section: { id: 2, label: "Race Details", isDisplayed: true },
                create: [{ enabled: true, size: { xs: 12, md: 6 }  }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0'], 
            },
			{ 
                label: 'Bib Name',
                type: 'text',
                order: { form: 3.1 }, 
                section: { id: 2, label: "Race Details" },
                create: [{ enabled: true, size: { xs: 12, md: 6 }  }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0'], 
            },
			{
                label: 'Parent Bib',
                type: 'switch',
                order: { form: 3.15 },
                section: { id: 2, label: "Race Details" },
                tableDefault: false,
                // update.1 is the Issue BIB form, which carries only receiver
                // fields — the parent bib is surfaced in its header instead.
                create: [{ enabled: true, size: { xs: 12, md: 6 } }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0'],
            },
			{
                label: 'Bib Tag ID',
                type: 'text',
                order: { form: 3.2 }, 
                table: { maxWidth: 130 },
                section: { id: 2, label: "Race Details" },
                create: [{ enabled: false, size: { xs: 12, md: 6 }  }], update: ['same_as:create.0', { enabled: false, size: { xs: 12, md: 12 } }], view: ['same_as:create.0'], 
            },
			{ 
                label: 'Race',
                type: 'select',
                section: { id: 2, label: "Race Details" },
                order: { table: 4, form: 4 }, 
                create: [{ enabled: true, size: { xs: 12, md: 12 }  }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0'], 
				list: get(raceKitRaceState),
				getRequest: getRaceListRequest,
            },
			{
                label: 'Expected Finish Time',
                type: 'mask',
                order: { form: 4.05 },
                section: { id: 2, label: "Race Details" },
                create: [{ enabled: true, size: { xs: 12, md: 6 }, mask: "99:99:99" }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0'],
            },
			{
                label: 'Remarks',
                type: 'text',
                order: { form: 4.1 },
                section: { id: 2, label: "Race Details" },
                create: [{ enabled: true, size: { xs: 12 }  }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0'],
            },
			{
                label: 'Highlight Remarks',
                type: 'switch',
                order: { form: 4.11 },
                section: { id: 2, label: "Race Details" },
                tableDefault: false,
                // update.1 is the Issue BIB form. The remark is shown there as a
                // banner above the receiver fields rather than as an editable
                // field, so the desk cannot blank it while handing over a bib.
                create: [{ enabled: true, size: { xs: 12, md: 6 } }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0'],
            },
			{ 
                label: 'Race Tees',
                type: 'repeater',
                order: { form: 5 },
                section: { id: 2, label: "Race Details" },
                create: [{ enabled: true, size: { xs: 12, md: 12 }, deleteBtnSize: 2, repeatLabel: false }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0'],
                children: [
                    { 
                		label: 'Tee',
                		type: 'select',
                		order: { form: 1 },
                		create: [{ enabled: true, size: { xs: 12, md: 6 }  }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0'],
						list: get(raceKitTeeState),
						getRequest: getTeeListRequest,
            		},
					{
                		label: 'Quantity',
                		type: 'number',
                		order: { form: 2 },
                		create: [{ enabled: true, size: { xs: 12, md: 3 }  }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0'],
            		},
					{
                		label: 'Is Parent',
                		type: 'switch',
                		order: { form: 3 },
                		create: [{ enabled: true, size: { xs: 12, md: 3 } }], update: ['same_as:create.0', { enabled: false }], view: ['same_as:create.0'],
            		},
					
                ]
            },
			{ 
                label: 'Bib Issued',
                type: 'switch',
                order: { form: 9 }, 
                section: { id: 3, label: "Issue Details", isDisplayed: true },
                create: [{ enabled: false }], update: ['same_as:create.0', { enabled: false }], view: [{ enabled: true, size: { xs: 12, md: 6 } }], 
            },
			{ 
                label: 'Bib Issuer',
                type: 'select',
                order: { form: 9.1 }, 
                section: { id: 3, label: "Issue Details" },
                create: [{ enabled: false }], update: ['same_as:create.0', { enabled: false }], view: [{ enabled: true, size: { xs: 12, md: 6 }, validations: 'hidden_if:BibIssued,0' }], 
            },
			{ 
                label: 'Tee Issued',
                type: 'switch',
                order: { form: 9.4 }, 
                section: { id: 3, label: "Issue Details" },
                create: [{ enabled: false }], update: ['same_as:create.0', { enabled: false }], view: [{ enabled: true, size: { xs: 12, md: 6 } }], 
            },
			{ 
                label: 'Tee Issuer',
                type: 'select',
                order: { form: 9.5 }, 
                section: { id: 3, label: "Issue Details" },
                create: [{ enabled: false }], update: ['same_as:create.0', { enabled: false }], view: [{ enabled: true, size: { xs: 12, md: 6 }, validations: 'hidden_if:TeeIssued,0' }], 
            },
			{ 
                label: 'Tee',
                field: 'tee_size',
                type: 'text',
                order: { table: 6, }, 
                section: { id: 3, label: "Issue Details" },
                table: { maxWidth: 130 },
                create: [{ enabled: false }], update: ['same_as:create.0', { enabled: false }], view: [{ enabled: false }], 
            },
			{ 
                id: "STATUS",
                label: 'Status',
                type: 'text',
                order: { table: 7, form: 9.9 }, 
                section: { id: 3, label: "Issue Details" },
                table: { maxWidth: 130, customRender: (value) => (
                    <Badge title={value} color={value==="ISSUED" ? "success" : (value === "BIB ISSUED" ? "warning" : "info")} className={"bg-"+(value==="ISSUED" ? "success" : (value === "BIB ISSUED" ? "warning" : "info"))+" font-size-14"} style={{padding: "5px 10px"}}>
                        {value}
                    </Badge> 
                ) },
                create: [{ enabled: false }], update: ['same_as:create.0', { enabled: false }], view: [{ enabled: true, size: { xs: 12, md: 12 } }], 
            },


            
            { 
                id: 'RN01',
                label: 'Receiver Name',
                type: 'text',
                order: { form: 10 }, 
                section: { id: 3, label: "Issue Details" },
                create: [{ enabled: false }], update: ['same_as:create.0', { enabled: true, size: { xs: 12, md: 12 }  }], view: [{ enabled: true, size: { xs: 12, md: 4 }, validations: 'hidden_if:BibIssued,0' }], 
            },
			{ 
                label: 'Receiver Id Number',
                type: 'text',
                order: { form: 11 }, 
                section: { id: 3, label: "Issue Details" },
                create: [{ enabled: false }], update: ['same_as:create.0', { enabled: true, size: { xs: 12, md: 12 }  }], view: [{ enabled: true, size: { xs: 12, md: 4 }, validations: 'hidden_if:BibIssued,0' }], 
            },
			{ 
                label: 'Receiver Phone',
                type: 'text',
                order: { form: 12 }, 
                section: { id: 3, label: "Issue Details" },
                create: [{ enabled: false }], update: ['same_as:create.0', { enabled: true, size: { xs: 12, md: 12 }  }], view: [{ enabled: true, size: { xs: 12, md: 4 }, validations: 'hidden_if:BibIssued,0' }], 
            },





            
            { 
                label: 'Chip Start Time',
                type: 'text',
                order: { form: 100 }, 
                section: { id: 4, label: "Timing Details", isDisplayed: true },
                create: [{ enabled: false }], update: [{ enabled: true, size: { xs: 12, md: 4 } }], view: ['same_as:update.0'], 
            },
            { 
                label: 'Chip Time',
                type: 'text',
                order: { form: 101 }, 
                section: { id: 4, label: "Timing Details" },
                create: [{ enabled: false }], update: [{ enabled: false }], view: [{ enabled: true, size: { xs: 12, md: 4 } }], 
            },
            { 
                label: 'Chip End Time',
                type: 'text',
                order: { form: 100.1 }, 
                section: { id: 4, label: "Timing Details" },
                create: [{ enabled: false }], update: [{ enabled: true, size: { xs: 12, md: 4 } }], view: ['same_as:update.0'], 
            },
            { 
                label: 'Gun Time',
                type: 'text',
                order: { form: 102 }, 
                section: { id: 4, label: "Timing Details" },
                create: [{ enabled: false }], update: [{ enabled: false }], view: [{ enabled: true, size: { xs: 12, md: 4 } }], 
            },
            { 
                label: 'Overall Place',
                type: 'number',
                order: { form: 103 }, 
                section: { id: 4, label: "Timing Details" },
                create: [{ enabled: false }], update: [{ enabled: false }], view: [{ enabled: true, size: { xs: 12, md: 4 } }], 
            },
            { 
                label: 'Gender Place',
                type: 'number',
                order: { form: 104 }, 
                section: { id: 4, label: "Timing Details" },
                create: [{ enabled: false }], update: [{ enabled: false }], view: [{ enabled: true, size: { xs: 12, md: 4 } }], 
            },
            { 
                label: 'Age Category Place',
                type: 'number',
                order: { form: 105 }, 
                section: { id: 4, label: "Timing Details" },
                create: [{ enabled: false }], update: [{ enabled: false }], view: [{ enabled: true, size: { xs: 12, md: 4 } }], 
            },
            { 
                label: 'Age Category',
                type: 'select',
                order: { form: 106 }, 
                section: { id: 4, label: "Timing Details" },
                create: [{ enabled: false }], update: [{ enabled: false }], view: [{ enabled: true, size: { xs: 12, md: 4 } }], 
            },
            { 
                label: 'Is Disqualified',
                type: 'switch',
                order: { form: 107 }, 
                section: { id: 4, label: "Timing Details" },
                create: [{ enabled: false }], update: [{ enabled: true, size: { xs: 12, md: 4 } }], view: ['same_as:update.0'], 
            },
            { 
                label: 'Timing Status',
                type: 'text',
                order: { form: 108 }, 
                section: { id: 4, label: "Timing Details" },
                create: [{ enabled: false }], update: [{ enabled: false }], view: [{ enabled: true, size: { xs: 12, md: 4 } }], 
            },

        ].filter((v)=>{
            const page = get(pageState)
            const permissions = get(permissionState)
            if(! v.permission){
                return true
            }
            let absolute = false;
            if(v.permission.substr(0,1) === ":"){
                absolute = true;
                v.permission = v.permission.substr(1)
            }
            v.permission = absolute ? v.permission : v.permission + "-" + page
            return permissions?.includes(v.permission)
        })
    )
})
